import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import {
  Apps as AppsIcon,
  ArrowForward as ArrowForwardIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  BusinessCenter as BusinessCenterIcon,
  Construction as ConstructionIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import {
  getApplicationKind,
  getApplicationRoute,
  getUserApplications,
  isAdminUser,
} from "modules/systems/services/application.service";
import { getFullAvatarUrl, getInitials } from "utils/imageUtils";
import "./PortalPage.css";
import { AppButton } from "components/common/buttons";

const getApplicationIcon = (application) => {
  const appName = getApplicationKind(application);

  if (appName === "system") return <AdminPanelSettingsIcon />;
  if (appName === "contract") return <BusinessCenterIcon />;
  if (appName === "subcontract") return <ConstructionIcon />;
  if (appName === "equipment") return <PrecisionManufacturingIcon />;
  return <AppsIcon />;
};

const getApplicationStatus = (application) => {
  const appName = getApplicationKind(application);
  // SUBCONTRACT
  if (appName === "system" || appName === "contract" || appName === "subcontract" || appName === "equipment") {
    return { label: "Sẵn sàng", color: "success" };
  }

  return { label: "Đang phát triển", color: "warning" };
};

const PortalPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAdminUser(user);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadApplications = async () => {
      if (!user?.userId) {
        setLoading(false);
        return;
      }

      try {
        const data = await getUserApplications(user.userId);
        if (isMounted) {
          setApplications(data);
          setError("");
        }
      } catch (loadError) {
        if (isMounted) {
          setError("Không thể tải danh sách ứng dụng được phân quyền.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadApplications();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const uniqueApplications = useMemo(() => {
    const map = new Map();

    applications.forEach((application) => {
      if (!map.has(application.applicationId)) {
        map.set(application.applicationId, application);
      }
    });

    return Array.from(map.values());
  }, [applications]);

  const handleOpenApplication = (application) => {
    navigate(getApplicationRoute(application), {
      state: { application },
    });
  };

  return (
    <Box className="portal-page">
      <Box className="portal-shell">
        <Box className="portal-topbar">
          <Box className="portal-brand">
            <img src="/logoXNTD.png" alt="Logo Xí nghiệp Trắc địa" />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Hệ thống quản lý
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Xí nghiệp Trắc địa
              </Typography>
            </Box>
          </Box>

          <AppButton
            variant="outlined"
            color="error"
            onClick={logout}
            startIcon={<LogoutIcon />}
          >
            Đăng xuất
          </AppButton>
        </Box>

        <Paper elevation={0} className="portal-user-panel">
          <Box className="portal-user-main">
            <Avatar
              src={getFullAvatarUrl(user?.avatarFilePath)}
              alt={user?.fullName || user?.userName}
              sx={{ width: 64, height: 64, fontSize: 24 }}
            >
              {getInitials(user?.fullName || user?.userName)}
            </Avatar>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Xin chào
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {user?.fullName || user?.userName}
              </Typography>
              <Box className="portal-user-meta">
                <Chip
                  icon={<PersonIcon />}
                  label={user?.role || "Chưa xác định vai trò"}
                  size="small"
                  color={isAdmin ? "primary" : "default"}
                />
                {user?.email && <Chip label={user.email} size="small" />}
              </Box>
            </Box>
          </Box>

          <Box className="portal-user-stats">
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {uniqueApplications.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isAdmin
                ? "ứng dụng toàn quyền truy cập"
                : "ứng dụng được phép truy cập"}
            </Typography>
          </Box>
        </Paper>

        <Box className="portal-section-header">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Ứng dụng của bạn
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isAdmin
                ? "Admin luôn có quyền cao nhất và truy cập tất cả ứng dụng."
                : "Portal chỉ hiển thị các ứng dụng bạn đã được cấp quyền."}
            </Typography>
          </Box>
        </Box>

        {loading && (
          <Box className="portal-loading">
            <CircularProgress />
            <Typography color="text.secondary">
              Đang tải danh sách ứng dụng...
            </Typography>
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && uniqueApplications.length === 0 && (
          <Alert severity="warning">
            Tài khoản của bạn chưa được phân quyền truy cập ứng dụng nào.
          </Alert>
        )}

        {!loading && !error && uniqueApplications.length > 0 && (
          <Box className="portal-app-grid">
            {uniqueApplications.map((application) => {
              const status = getApplicationStatus(application);

              return (
                <button
                  type="button"
                  key={application.applicationId}
                  className="portal-app-card"
                  onClick={() => handleOpenApplication(application)}
                >
                  <Box className="portal-app-card-top">
                    <span className="portal-app-icon">
                      {getApplicationIcon(application)}
                    </span>
                    <Chip
                      label={status.label}
                      color={status.color}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  <Box className="portal-app-card-body">
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {application.applicationName || "Ứng dụng không xác định"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {application.description || "Không có mô tả"}
                    </Typography>
                  </Box>

                  <Box className="portal-app-card-action">
                    <Typography variant="button">Mở ứng dụng</Typography>
                    <ArrowForwardIcon fontSize="small" />
                  </Box>
                </button>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PortalPage;
